import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Apartment as CustomersIcon,
  Toll as CreditsIcon,
  Policy as AuditIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AuthShell from '../components/AuthShell';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function formatLoginError(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  const detailStr =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d) => (typeof d === 'object' && d?.msg ? d.msg : String(d))).join(' ')
        : '';

  if (status === 401 || status === 403) {
    const lower = detailStr.toLowerCase();
    if (
      !detailStr ||
      lower.includes('invalid') ||
      lower.includes('credential') ||
      lower.includes('incorrect')
    ) {
      return 'Incorrect email or password. Check that both are correct and try again.';
    }
    return detailStr;
  }
  if (detailStr) return detailStr;
  if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
    return `Cannot reach ${API_URL}. Start the backend on port 8000, or set REACT_APP_API_URL. Using a LAN IP? Add that URL to CORS_ORIGINS in server/backend/.env.`;
  }
  if (err.message) return err.message;
  return 'Login failed';
}

const GOLD = '#e5c04a';

// The shape of what an operator runs — customer › office — with no live data.
function HierarchyPreview() {
  const rows = [
    { depth: 0, label: 'Enterprise', w: '46%' },
    { depth: 1, label: 'Organisation', w: '40%' },
    { depth: 2, label: 'Region', w: '34%' },
    { depth: 3, label: 'Office', w: '30%' },
  ];
  return (
    <Box
      sx={{
        maxWidth: 460,
        p: 2,
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.12)',
        bgcolor: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.5)',
      }}
    >
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, color: GOLD, mb: 1.5 }}>
        CUSTOMER HIERARCHY
      </Typography>
      {rows.map(({ depth, label, w }) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, pl: depth * 2.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', border: `1.5px solid ${GOLD}`, opacity: 1 - depth * 0.15 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, width: 96, color: 'rgba(255,255,255,0.85)' }}>{label}</Typography>
          <Box sx={{ height: 7, width: w, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.14)' }} />
        </Box>
      ))}
    </Box>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      portalName="Admin portal"
      panelBackground="linear-gradient(155deg, #020617 0%, #0f172a 55%, #1e293b 100%)"
      accent={GOLD}
      headlineFont='"Fraunces", Georgia, serif'
      eyebrow="Platform operations"
      headline={<>Run the platform<br /><Box component="span" sx={{ color: GOLD }}>behind every clinic.</Box></>}
      body="Onboard customers, shape their sites, and keep AI capacity where it’s needed."
      points={[
        { icon: <CustomersIcon />, title: 'Customers & standalone clinics', text: 'Open enterprises, organisations and offices, and hand them to their admins.' },
        { icon: <CreditsIcon />, title: 'AI session credits', text: 'Allocate and top up credit pools across a customer’s tree.' },
        { icon: <AuditIcon />, title: 'Support, on the record', text: 'Step into an account to help its admins; sign-ins and patient-data access are audit-logged.' },
      ]}
      preview={<HierarchyPreview />}
    >
      <Typography variant="overline" sx={{ color: 'primary.dark', fontWeight: 700, letterSpacing: 1.2 }}>
        Super admin
      </Typography>
      <Typography variant="h4" component="h2" sx={{ mt: 0.5, mb: 1 }}>
        Sign in
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        ScribeLive platform management.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <TextField fullWidth label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
          InputLabelProps={{ shrink: true }} margin="normal" required autoFocus />
        <TextField fullWidth label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <HideIcon /> : <ShowIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          value={password}
          onChange={(e) => setPassword(e.target.value)} InputLabelProps={{ shrink: true }} margin="normal" required />
        <Button fullWidth type="submit" variant="contained" size="large"
          disabled={loading} sx={{ mt: 3, py: 1.5, fontWeight: 700 }}>
          {loading ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>

      <Box sx={{ mt: 3, display: 'flex', gap: 1, alignItems: 'flex-start', color: 'text.secondary' }}>
        <LockIcon sx={{ fontSize: 16, mt: '2px' }} />
        <Typography variant="body2">
          Restricted to ScribeLive staff. Clinic and patient accounts sign in on their own portals.
        </Typography>
      </Box>
    </AuthShell>
  );
}
